import React, { useEffect, useMemo, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import type { PedidoCompleto } from '@/services/orderApiService';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pedido: PedidoCompleto | null;
  isLoading?: boolean;
  error?: string | null;
  tableNumber?: number | string;
};

const moneyNoSymbol = (value: number) =>
  (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const moneyWithSymbol = (value: number) => `R$ ${moneyNoSymbol(value)}`;

const formatPedidoDateTime = (iso: string) => {
  const d = new Date(iso);
  const date = d.toLocaleDateString('pt-BR');
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return { date, time };
};

export default function PedidoPreviewDialog({
  open,
  onOpenChange,
  pedido,
  isLoading = false,
  error = null,
  tableNumber,
}: Props) {
  const barcodeSvgRef = useRef<SVGSVGElement | null>(null);

  const vendaId = pedido?.venda?.id;
  const tableNumberText =
    tableNumber === 0 || tableNumber ? String(tableNumber).trim() : '';

  useEffect(() => {
    if (!open) return;
    if (!barcodeSvgRef.current) return;
    if (!vendaId) return;

    try {
      JsBarcode(barcodeSvgRef.current, String(vendaId), {
        format: 'CODE128',
        displayValue: false,
        height: 48,
        width: 1.6,
        margin: 2,
      });
    } catch {
      // ignora erro de renderização do barcode
    }
  }, [open, vendaId]);

  const view = useMemo(() => {
    if (!pedido) return null;

    const empresaNome = pedido.empresa?.nomeFantasia || pedido.empresa?.razaoSocial || '';
    const empresaCnpj = pedido.empresa?.cnpj ? `CNPJ: ${pedido.empresa.cnpj}` : '';
    const empEnd = pedido.empresa?.endereco
      ? [
          pedido.empresa.endereco.logradouro,
          pedido.empresa.endereco.numero,
          pedido.empresa.endereco.complemento,
        ]
          .filter(Boolean)
          .join(', ')
      : '';
    const empCid = pedido.empresa?.endereco
      ? [pedido.empresa.endereco.municipio, pedido.empresa.endereco.uf].filter(Boolean).join(' - ')
      : '';

    const { date, time } = formatPedidoDateTime(pedido.venda.data);

    const clienteNome = (pedido.venda as any)?.cliente?.nome || pedido.cliente?.nome || 'CONSUMIDOR';
    const clienteDoc = (pedido.venda as any)?.cliente?.documento || pedido.cliente?.documento || '';
    const clienteLinha = clienteDoc ? `${clienteNome} - ${clienteDoc}` : clienteNome;

    const cliEnd = ((pedido.venda as any)?.cliente?.enderecoEntrega || pedido.cliente?.endereco)
      ? [
          ((pedido.venda as any)?.cliente?.enderecoEntrega?.logradouro || (pedido.cliente as any)?.endereco?.logradouro),
          ((pedido.venda as any)?.cliente?.enderecoEntrega?.numero || (pedido.cliente as any)?.endereco?.numero),
          ((pedido.venda as any)?.cliente?.enderecoEntrega?.complemento || (pedido.cliente as any)?.endereco?.complemento),
          ((pedido.venda as any)?.cliente?.enderecoEntrega?.bairro || (pedido.cliente as any)?.endereco?.bairro),
          ((pedido.venda as any)?.cliente?.enderecoEntrega?.cidade || (pedido.cliente as any)?.endereco?.municipio),
          ((pedido.venda as any)?.cliente?.enderecoEntrega?.estado || (pedido.cliente as any)?.endereco?.uf),
        ]
          .filter(Boolean)
          .join(', ')
      : '';

    const itensVenda = ((pedido.venda as any)?.itens || pedido.itens || []) as any[];
    const totalItens = Number((pedido.venda as any)?.totalItens) || itensVenda.reduce((s, x) => s + (Number(x.totalFinal) || 0), 0);

    return {
      empresaNome,
      empresaCnpj,
      empEnd,
      empCid,
      date,
      time,
      clienteLinha,
      cliEnd,
      totalItens,
      acrescimo: pedido.venda.totalAcrescimo || 0,
      desconto: pedido.venda.totalDesconto || 0,
      totalFinal: pedido.venda.totalFinal || 0,
    };
  }, [pedido]);

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const handleBrowserPrint = () => {
    if (!pedido || !view || isLoading || error) return;
    if (!vendaId) return;

    const barcodeMarkup = barcodeSvgRef.current?.outerHTML || '';

    const empresaNome = escapeHtml(view.empresaNome || '');
    const empresaCnpj = escapeHtml(view.empresaCnpj || '');
    const empEnd = escapeHtml(view.empEnd || '');
    const empCid = escapeHtml(view.empCid || '');
    const clienteLinha = escapeHtml(view.clienteLinha || '');
    const cliEnd = escapeHtml(view.cliEnd || '');

    const itensHtml = (((pedido.venda as any)?.itens || pedido.itens || []) as any[])
      .map((i) => {
        const qtd = (i.quantidade || 1).toLocaleString('pt-BR');
        const nome = escapeHtml(i.produtoNome || '');
        const total = moneyNoSymbol(i.totalFinal || 0);

        return `
          <div class="row">
            <div class="left">${escapeHtml(qtd)} ${nome}</div>
            <div class="right">${escapeHtml(total)}</div>
          </div>
        `;
      })
      .join('');

    const pagamentosHtml = (((pedido.venda as any)?.pagamentos || pedido.pagamentos || []) as any[])
      .map((p) => {
        const desc = escapeHtml((p.descricao || `Forma ${p.formaPagamentoID}`) as string);
        const val = escapeHtml(moneyWithSymbol(p.valor || 0));
        return `
          <div class="row">
            <div class="left">${desc}</div>
            <div class="right">${val}</div>
          </div>
        `;
      })
      .join('');

    const pagamentosList = (((pedido.venda as any)?.pagamentos || pedido.pagamentos || []) as any[]);
    const hasPagamentos = pagamentosList.length > 0;

    const tableLineHtml = tableNumberText
      ? `<div class="center bold big" style="margin: 10px 0 6px;">COMANDA/MESA: ${escapeHtml(tableNumberText)}</div>`
      : '';

    const html = `
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Pedido ${escapeHtml(String(vendaId))}</title>
          <style>
            @page { margin: 12mm; }
            html, body { padding: 0; margin: 0; }
            body {
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
              color: #111;
            }
            .ticket {
              width: 100%;
              max-width: 420px;
              margin: 0 auto;
              font-size: 12px;
              line-height: 1.35;
            }
            .center { text-align: center; }
            .bold { font-weight: 700; }
            .big { font-size: 14px; }
            .sep { border-top: 1px dashed #999; margin: 10px 0; }
            .sep-solid { border-top: 1px solid #ddd; margin: 8px 0; }
            .row { display: flex; gap: 12px; justify-content: space-between; }
            .left { flex: 1; min-width: 0; word-break: break-word; }
            .right { white-space: nowrap; }
            svg { display: block; max-width: 100%; height: auto; }
            .note { margin-top: 12px; font-size: 11px; }
            @media print {
              button { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="center bold">${empresaNome}</div>
            ${empresaCnpj ? `<div class="center">${empresaCnpj}</div>` : ''}
            ${empEnd ? `<div class="center">${empEnd}</div>` : ''}
            ${empCid ? `<div class="center">${empCid}</div>` : ''}

            <div class="sep"></div>

            <div>Pedido #${escapeHtml(String(pedido.venda.id))} ${escapeHtml(view.date)} ${escapeHtml(view.time)}</div>
            <div>${clienteLinha}</div>
            ${cliEnd ? `<div>${cliEnd}</div>` : ''}

            <div class="sep"></div>

            <div>${itensHtml}</div>

            <div class="sep"></div>

            <div class="row"><div class="left">Total itens (=)</div><div class="right">${escapeHtml(moneyNoSymbol(view.totalItens))}</div></div>
            <div class="row"><div class="left">Acréscimo (+)</div><div class="right">${escapeHtml(moneyNoSymbol(view.acrescimo))}</div></div>
            <div class="row"><div class="left">Desconto (-)</div><div class="right">${escapeHtml(moneyNoSymbol(view.desconto))}</div></div>
            <div class="sep-solid"></div>
            <div class="row bold"><div class="left">TOTAL (=)</div><div class="right">${escapeHtml(moneyNoSymbol(view.totalFinal))}</div></div>

            ${hasPagamentos ? `<div style="margin-top: 14px;" class="bold">Forma de pagamento</div>
                <div style="margin-top: 4px;">${pagamentosHtml}</div>` : ''}

            <div style="margin-top: 16px;" class="center">
              ${tableLineHtml}
              ${barcodeMarkup}
              <div style="margin-top: 4px;">ID. do pedido: ${escapeHtml(String(vendaId))}</div>
            </div>

            <div class="note center">*** Este comprovante não é documento fiscal ***</div>
          </div>
          <script>
            window.addEventListener('load', function () {
              window.focus();
              window.print();
            });
            window.addEventListener('afterprint', function () {
              window.close();
            });
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Não foi possível abrir a janela de impressão. Verifique bloqueio de pop-ups.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pré-visualização do Pedido</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[70vh] pr-4">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
            </div>
          )}

          {!isLoading && error && (
            <div className="text-sm text-destructive py-2">{error}</div>
          )}

          {!isLoading && !error && pedido && view && (
            <div className="border rounded-md p-4 font-mono text-sm">
              <div className="text-center font-bold">{view.empresaNome}</div>
              {view.empresaCnpj && <div className="text-center">{view.empresaCnpj}</div>}
              {view.empEnd && <div className="text-center">{view.empEnd}</div>}
              {view.empCid && <div className="text-center">{view.empCid}</div>}

              <div className="my-3 border-t border-dashed" />

              <div>
                Pedido #{pedido.venda.id} {view.date} {view.time}
              </div>
              <div>{view.clienteLinha}</div>
              {view.cliEnd && <div>{view.cliEnd}</div>}

              <div className="my-3 border-t border-dashed" />

              <div className="space-y-1">
                {((((pedido.venda as any)?.itens || pedido.itens || []) as any[])).map((i, idx) => (
                  <div key={`${i.produtoID}-${idx}`} className="flex justify-between gap-3">
                    <div className="flex-1 break-words">
                      {(i.quantidade || 1).toLocaleString('pt-BR')} {i.produtoNome || ''}
                    </div>
                    <div className="whitespace-nowrap">{moneyNoSymbol(i.totalFinal || 0)}</div>
                  </div>
                ))}
              </div>

              <div className="my-3 border-t border-dashed" />

              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Total itens (=)</span>
                  <span>{moneyNoSymbol(view.totalItens)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Acréscimo (+)</span>
                  <span>{moneyNoSymbol(view.acrescimo)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Desconto (-)</span>
                  <span>{moneyNoSymbol(view.desconto)}</span>
                </div>
                <div className="my-2 border-t" />
                <div className="flex justify-between font-bold">
                  <span>TOTAL (=)</span>
                  <span>{moneyNoSymbol(view.totalFinal)}</span>
                </div>
              </div>

              {(((pedido.venda as any)?.pagamentos || pedido.pagamentos) && (((pedido.venda as any)?.pagamentos || pedido.pagamentos) as any[]).length > 0) && (
                 <>
                   <div className="mt-4 font-bold">Forma de pagamento</div>
                   <div className="space-y-1">
                     {((((pedido.venda as any)?.pagamentos || pedido.pagamentos || []) as any[])).map((p) => (
                       <div key={p.id} className="flex justify-between">
                         <span>{(p.descricao || `Forma ${p.formaPagamentoID}`) as string}</span>
                         <span>{moneyWithSymbol(p.valor || 0)}</span>
                       </div>
                     ))}
                   </div>
                 </>
               )}

              {vendaId && (
                <div className="mt-5 flex flex-col items-center">
                  {tableNumberText && (
                    <div className="mb-2 text-base font-bold">COMANDA/MESA: {tableNumberText}</div>
                  )}
                  <svg ref={barcodeSvgRef} />
                  <div className="mt-1">ID. do pedido: {vendaId}</div>
                </div>
              )}

              <div className="mt-4 text-center text-xs">*** Este comprovante não é documento fiscal ***</div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={handleBrowserPrint} disabled={!pedido || !!error || isLoading}>
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
